import { Link } from "react-router-dom";
import { FaNewspaper } from "react-icons/fa";
import { formatPublicationDateLabel } from "../utils/publicationUtils";
import {
  getPublicationType,
  shortVenueLabel,
} from "../utils/pillarContent";
import { researchPillars } from "../data/researchPillars";
import PillarGraphic from "./PillarGraphic";
import { formatHtml } from "../utils/formatHtml";

const pillarById = Object.fromEntries(
  researchPillars.map((pillar) => [pillar.id, pillar]),
);

const highlightName = (authorsStr) => {
  return authorsStr.replace(
    // match "Kellie Sim" or "Kellie Yu Hui Sim"
    /(\bKellie\s+Sim\b|\bKellie\s+Yu\s+Hui\s+Sim\b)/g,
    (match) =>
      `<span class="font-semibold underline decoration-green/70 underline-offset-2">${match.trim()}</span>`,
  );
};

const getPublicationLabel = ({ venue, year, month, links, publicationType }) => {
  if (publicationType === "Preprint") {
    const dateLabel = formatPublicationDateLabel({ year, month, links });
    return dateLabel ? `arXiv ${dateLabel}` : "arXiv";
  }

  const venueText = shortVenueLabel(venue);
  const yearLabel = year ? String(year) : null;

  if (!venueText) {
    return yearLabel ? `${publicationType} ${yearLabel}` : publicationType;
  }
  return yearLabel
    ? `${venueText} ${yearLabel} • ${publicationType}`
    : `${venueText} • ${publicationType}`;
};

const PillarIconLinks = ({ pillarIds = [] }) => {
  const ids = pillarIds.filter((id) => pillarById[id]);
  if (!ids.length) return null;

  return (
    <div className="flex shrink-0 items-center gap-1">
      {ids.map((id) => {
        const pillar = pillarById[id];
        return (
          <Link
            key={id}
            to={`/home?view=agenda&pillar=${id}`}
            title={pillar.bigGoal || pillar.title}
            aria-label={`Open ${pillar.bigGoal || pillar.id} research question`}
            className="block h-7 w-9 overflow-hidden rounded border border-gray-200 transition-colors hover:border-green/60 dark:border-gray-700 dark:hover:border-green/60"
          >
            <PillarGraphic pillarId={id} className="h-full w-full" />
          </Link>
        );
      })}
    </div>
  );
};

const PublicationCard = ({
  title,
  authors,
  venue,
  year,
  month,
  authorLinks = {},
  tags = [],
  comments,
  press,
  image,
  links = {},
  pillarIds = [],
}) => {
  const publicationType = getPublicationType({ venue, tags });
  const publicationLabel = getPublicationLabel({
    venue,
    year,
    month,
    links,
    publicationType,
  });
  const authorList = authors
    .replace(/\s+&\s+/g, ", ")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);

  const renderAuthors = () =>
    authorList.map((name, idx) => {
      const key = `${name}-${idx}`;
      const formattedName = highlightName(name);
      const link = authorLinks[name];
      const suffix = idx < authorList.length - 1 ? ", " : "";

      if (link) {
        return (
          <span key={key}>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green dark:text-green-dark underline underline-offset-2"
            >
              <span dangerouslySetInnerHTML={{ __html: formattedName }} />
            </a>
            {suffix}
          </span>
        );
      }

      return (
        <span key={key}>
          <span dangerouslySetInnerHTML={{ __html: formattedName }} />
          {suffix}
        </span>
      );
    });

  const IMAGE_BOX_CLASSES =
    "w-full md:w-32 lg:w-36 min-w-[5rem] max-w-[9rem] min-h-[6rem] max-h-32 flex items-center justify-center bg-white dark:bg-darkBg p-2 rounded border border-gray-200/70 dark:border-gray-800/70";
  const IMAGE_CLASSES =
    "w-full h-full max-h-28 max-w-full object-contain rounded";

  return (
    <div className="flex flex-col md:flex-row gap-5 py-5 border-b border-gray-200 dark:border-gray-800">
      {image && (
        <div className={IMAGE_BOX_CLASSES}>
          <img src={image} alt={title} className={IMAGE_CLASSES} />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="inline-flex items-center py-0.5 rounded-md text-sm font-semibold tracking-wide text-gray-700 dark:text-gray-300">
            [{publicationLabel}]
          </p>
          <PillarIconLinks pillarIds={pillarIds} />
        </div>

        <h3 className="text-base font-semibold text-lightText dark:text-darkText leading-snug">
          {title}
        </h3>

        <p className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          {renderAuthors()}
        </p>

        {comments && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            {comments}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {links.paper && (
            <a
              href={links.paper}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green dark:text-green-dark font-medium underline underline-offset-2"
            >
              DOI / Publisher Link
            </a>
          )}
          {links.paper && links.preprint && (
            <span className="text-gray-500 dark:text-gray-400">•</span>
          )}
          {links.preprint && (
            <a
              href={links.preprint}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green dark:text-green-dark font-medium underline underline-offset-2"
            >
              arXiv Preprint
            </a>
          )}
          {links.poster && (links.paper || links.preprint) && (
            <span className="text-gray-500 dark:text-gray-400">•</span>
          )}
          {links.poster && (
            <a
              href={links.poster}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green dark:text-green-dark font-medium underline underline-offset-2"
            >
              Poster
            </a>
          )}
          {links.slides && (links.paper || links.preprint || links.poster) && (
            <span className="text-gray-500 dark:text-gray-400">•</span>
          )}
          {links.slides && (
            <a
              href={links.slides}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green dark:text-green-dark font-medium underline underline-offset-2"
            >
              Slides
            </a>
          )}
        </div>
        {press ? (
          <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold leading-relaxed text-orange-800 dark:text-orange-300">
            <FaNewspaper
              size={14}
              className="mt-0.5 shrink-0"
              aria-hidden="true"
              title="Press and Social Media"
            />
            <span
              className="min-w-0 [&_a]:font-semibold [&_a]:text-orange-700 [&_a]:underline [&_a]:decoration-orange-400/70 [&_a]:underline-offset-2 [&_a]:hover:text-orange-500 dark:[&_a]:text-orange-200 dark:[&_a]:hover:text-orange-100"
              dangerouslySetInnerHTML={{ __html: formatHtml(press) }}
            />
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default PublicationCard;
